export type PageRuntimeState = Record<string, unknown>;

export const RUNTIME_KEY = '__unisubsPageRuntime__';
export const RUNTIME_READY_EVENT = 'unisubs-runtime-ready';

function createDefaultRuntime(): PageRuntimeState {
  return {
    site: '',
    metadata: {},
    selections: {},
    selected: {},
    storage: {},
    functions: {},
    uploaded: {},
    downloaded: {},
    modules: {},
    constants: {
      asrLastItemDurationMs: 500,
      potLookup: {},
    },
    variables: {
      delayedTimeInSecondMain: 0,
      delayedTimeInSecondSecond: 0,
      hiddingMain: false,
      hiddingSecond: false,
      mainPriority: undefined,
      secondPriority: undefined,
      dualModeMain: false,
      dualModeSecond: false,
      micMonitoring: false,
    },
    learning: {
      clientX: undefined,
      isMain: undefined,
      selectedText: '',
      selectedNode: undefined,
      deck: { id: 'uncategorized', name: '' },
      decks: [],
      isLoadingTTS: false,
      isLoadingTranslate: false,
      isLoadingGPT: false,
      playBackRate: 'medium',
    },
    speaking: {
      isRecording: false,
      isInitializingRecording: false,
      recording: null,
    },
    subtitleReader: {
      enable: false,
      isLoading: false,
    },
    translationLanguages: [],
    i18n: {},
    videoElement: undefined,
    shortcutRefElement: undefined,
    baselineRefElement: undefined,
    canvaRefElement: undefined,
    kuroshiro: undefined,
    kuroshiroAnalyzer: undefined,
    debug: false,
  };
}

type RuntimeHost = typeof globalThis & {
  [RUNTIME_KEY]?: PageRuntimeState;
  unisubs?: PageRuntimeState;
  dispatchEvent?: (event: Event) => boolean;
};

function getRuntimeHost(): RuntimeHost {
  return globalThis as RuntimeHost;
}

function emitRuntimeReady(host: RuntimeHost): void {
  try {
    if (typeof host.dispatchEvent === 'function' && typeof CustomEvent === 'function') {
      host.dispatchEvent(new CustomEvent(RUNTIME_READY_EVENT));
    }
  } catch {
    // Non-DOM hosts (tests) — ignore.
  }
}

export function installPageRuntime(): PageRuntimeState {
  const host = getRuntimeHost();
  const created = !host[RUNTIME_KEY];
  if (created) {
    host[RUNTIME_KEY] = createDefaultRuntime();
  }
  if (host.unisubs !== host[RUNTIME_KEY]) {
    host.unisubs = host[RUNTIME_KEY];
  }
  if (created) {
    emitRuntimeReady(host);
  }
  return host[RUNTIME_KEY]!;
}

/** Idempotent: installs if missing so page scripts never race global.min.js. */
export function getPageRuntime(): PageRuntimeState {
  const host = getRuntimeHost();
  if (!host[RUNTIME_KEY]) {
    return installPageRuntime();
  }
  return host[RUNTIME_KEY]!;
}
